/**
 * Pure geometry for the Complete the Hand "held hand" fan.
 *
 * Cards pivot about a point below the hand's center. The middle card is
 * vertical and the outer cards rotate away symmetrically, giving the classic
 * upward-arc look of cards held in a hand. The arc is shifted up by half its
 * own height so its vertical span is centered on (centerX, centerY) rather than
 * hanging below it.
 *
 * Coordinates are absolute (relative to the same root the overlay measures
 * against) and refer to each card's CENTER, matching the center-based card
 * animation in CthGame.
 */

export type FanSlot = {
  cx: number
  cy: number
  rotateDeg: number
}

export type FanParams = {
  /** Center of the hand area (e.g. the measured fan anchor's center). */
  centerX: number
  centerY: number
  /** Distance from the pivot to each card center. Larger = flatter arc. */
  radius: number
  /** Degrees between adjacent cards. */
  angleStepDeg: number
}

/** Number of cards in the fixed fan footprint: 2 starting + 3 picks. */
export const FAN_SIZE = 5

/** Default tuning — outer cards end at ±(FAN_SIZE-1)/2 * step. */
export const FAN_ANGLE_STEP_DEG = 14

const DEG_TO_RAD = Math.PI / 180

/**
 * Position + rotation for the card at `index` (0..count-1) within a fan of
 * `count` cards. The pivot sits `radius` below center so index `(count-1)/2`
 * lands directly above it.
 */
export function getFanSlot(index: number, count: number, params: FanParams): FanSlot {
  const { centerX, centerY, radius, angleStepDeg } = params
  const mid = (count - 1) / 2
  const angleDeg = (index - mid) * angleStepDeg
  const angleRad = angleDeg * DEG_TO_RAD

  // Pivot is `radius` below the center point; cards swing up from it.
  const pivotX = centerX
  const pivotY = centerY + radius

  // The arc's vertical span runs from the middle card (top) down to the outer
  // cards. Lift everything by half that span so the fan is centered on centerY.
  const maxAngleRad = mid * angleStepDeg * DEG_TO_RAD
  const verticalCenterShift = (radius - radius * Math.cos(maxAngleRad)) / 2

  return {
    cx: pivotX + radius * Math.sin(angleRad),
    cy: pivotY - radius * Math.cos(angleRad) - verticalCenterShift,
    rotateDeg: angleDeg
  }
}
